import './PuxSection.scss'

import React, { FunctionComponent } from 'react'
import { useEditorStatus } from 'Root/Shared/utils/edit/useEditorStatus'
import {
  getAnimation,
  getAnimationWrapper,
} from 'Root/Shared/utils/getAnimation'
import WidgetBuilder, {
  WidgetAnimationPart,
  WidgetData,
  WidgetProperitesPart,
} from 'Shared/components/builders/WidgetBuilder'
import PuxModal from 'Shared/components/PuxModal/PuxModal'

import EasySoftwareEditorContainer from '../EasySoftwareEditorContainer/EasySoftwareEditorContainer'
import PuxButton, { PuxButtonType } from '../PuxButton/PuxButton'
import { PuxWysiwygHtmlBodyType } from '../PuxWysiwyg/PuxWysiwyg'

export type SectionHorizontalAlign =
  | 'Section--horizontalCenter'
  | 'Section--horizontalBottom'

export type SectionVerticalAlign = 'Section--verticalCenter'

export type SectionModalVariant = 'default' | 'slim'

export interface PuxSectionType {
  pageContentItemId: string
  editorPath: string
  contentType: 'PuxSection'
  contentItemId?: string
  sectionUseContainer: boolean
  sectionPerex: PuxWysiwygHtmlBodyType
  sectionHorizontalAlign: SectionHorizontalAlign
  sectionVerticalAlign: SectionVerticalAlign
  sectionIsModal?: boolean
  sectionModalId?: string
  sectionModalVariant?: SectionModalVariant
  sectionButton: PuxButtonType
  noSpacing: boolean
  template: WidgetData[]
  puxBackgroundDefinition: {
    backgroundDefinitionPartColor: 'Section--light'
  }
  flow: {
    widgets: WidgetData[]
  }
  puxWidgetProperties: WidgetProperitesPart
  puxWidgetAnimation: WidgetAnimationPart
}

const PuxSection: FunctionComponent<PuxSectionType> = (props) => {
  const isEditor = useEditorStatus()

  const { template } = props

  if (props.template?.length > 1) {
    props.flow.widgets.sort((widget1, widget2) => {
      const index1 = template.findIndex(
        (x) => x.contentItemId === widget1.contentItemId
      )
      const index2 = template.findIndex(
        (x) => x.contentItemId === widget2.contentItemId
      )
      return index1 - index2
    })
  }

  const widgetProperties = props.puxWidgetProperties
  const classes: string[] = [`Section`]
  const containerClasses: string[] = [`Container`]
  const ids: string[] = []
  
  if (props.puxBackgroundDefinition?.backgroundDefinitionPartColor) {
    classes.push(props.puxBackgroundDefinition.backgroundDefinitionPartColor)
  }

  if (widgetProperties.widgetPropertiesPartMarginBottom) {
    classes.push(widgetProperties.widgetPropertiesPartMarginBottom)
  }

  if (widgetProperties.widgetPropertiesPartMarginTop) {
    classes.push(widgetProperties.widgetPropertiesPartMarginTop)
  }

  if (widgetProperties.widgetPropertiesPartPaddingBottom) {
    classes.push(widgetProperties.widgetPropertiesPartPaddingBottom)
  }

  if (widgetProperties.widgetPropertiesPartPaddingTop) {
    classes.push(widgetProperties.widgetPropertiesPartPaddingTop)
  }

  if (widgetProperties.widgetPropertiesPartMobileOrder) {
    classes.push(` mobileOrder-${widgetProperties.widgetPropertiesPartMobileOrder}`)
  }

  if (!props.sectionUseContainer) {
    containerClasses.push(`Container--full`)
  }

  if (widgetProperties.widgetPropertiesPartID)
    ids.push(widgetProperties.widgetPropertiesPartID)

  if (props.sectionHorizontalAlign) classes.push(props.sectionHorizontalAlign)

  if (props.sectionVerticalAlign) classes.push(props.sectionVerticalAlign)

  const widgetAnimation = getAnimation(
    props.puxWidgetAnimation?.widgetAnimationPartType
  )

  const wysiwig = (
    <EasySoftwareEditorContainer
      className='Section-perex'
      content={props.sectionPerex}
      wysiwygId={props.contentItemId}
      pageContentItemId={props.pageContentItemId}
      editorPath={props.editorPath + `,puxSection,sectionPerex,html`}
    />
  )

  const button = (
    <PuxButton
      {...props.sectionButton}
      isEditor={isEditor ? true : false}
      contentItemId={props.contentItemId}
      editorPath={props.editorPath}
      pageContentItemId={props.pageContentItemId}
    />
  )

  const content = (
    <section className={classes.join(` `)} id={ids.join(` `)}>
      <div className={containerClasses.join(` `)}>
        {props.sectionPerex?.html &&
          getAnimationWrapper(
            wysiwig,
            props.puxWidgetAnimation?.widgetAnimationPartIsEnabled,
            widgetAnimation,
            props.puxWidgetAnimation?.widgetAnimationPartFraction,
            props.puxWidgetAnimation?.widgetAnimationPartDuration,
            props.puxWidgetAnimation?.widgetAnimationPartDelay
          )}
        <div className='Section-grid'>
          <WidgetBuilder
            widgetBuilderData={props.flow.widgets}
            pageContentItemId={props.pageContentItemId}
            editorPath={props.editorPath}
          />
        </div>
        {props.sectionButton?.buttonLink.text[0] && (
          <div className='Section-button'>
            {getAnimationWrapper(
              button,
              props.puxWidgetAnimation?.widgetAnimationPartIsEnabled,
              widgetAnimation,
              props.puxWidgetAnimation?.widgetAnimationPartFraction,
              props.puxWidgetAnimation?.widgetAnimationPartDuration,
              props.puxWidgetAnimation?.widgetAnimationPartDelay
            )}
          </div>
        )}
      </div>
    </section>
  )

  if (
    props.sectionIsModal &&
    typeof props.sectionModalId === `string` &&
    props.sectionModalId.length > 0
  ) {
    return (
      <PuxModal id={props.sectionModalId} variant={props.sectionModalVariant}>
        {content}
      </PuxModal>
    )
  } else {
    return content
  }
}

export default PuxSection
